import Setting from "@/types/Setting.interface"

interface State {
    settings: Setting[]
}

const defaults = () => {
    return {
        settings: [],         //  An array of all settings
    }
}

export default {
    state: defaults(),
    mutations: {
        settingsPush(state: State, settings: Setting[]): void {
            state.settings = window.removeDuplicatesById(state.settings, settings)
        },
        settingsReset(state: State): void {
            Object.assign(state, defaults())
        },
        settingsSet(state: State, settings: Setting[]): void {
            state.settings = settings
        },
    },
    actions: {
        settingsGet({ commit, getters }): Promise<boolean> {
            //  Assemble the API endpoint
            const url = `${getters.api}/settings`

            //  Send the request
            return window.axios.get(url).then(response => {
                //  Set our settings in the store
                commit('settingsSet', response.data)

                //  Return true because it was successful
                return true
            }).catch(() => null)
        },
        settingsPut({ commit, getters }, setting: Setting): Promise<Setting> {
            //  Assemble the API endpoint
            const url = `${getters.api}/settings`

            //  Send the request
            return window.axios.put(url, setting).then(response => {
                //  Update our setting
                commit('settingsPush', [response.data])

                //  REturn our updated setting
                return response.data
            }).catch(() => null)
        }
    },
    getters: {
        settingDescription: () => (name: string): string => {
            //  Return the appropriate description based on our map
            return {
                blockAnonymousCalls: 'Prevent anonymous calls to your workspace from forwarding and creating a contact.',
                conversationAssignment: 'Automatically assign conversations that are unassigned to the current user when sending messages.',
                onlyReceiveAssigned: "Teammates get notifications only for assigned conversations. When disabled, unassigned conversations notify the entire team.",
                showMessagePreview: 'Sending a message in the inbox will pop a preview of your message before it gets sent.',
                unassignOnClose: "Teammates will be unassigned from a conversation when it's closed.",
                showCreditUsage: "Show the credit usage indicator in the App toolbar.",
                hotKeysEnabled: "Users can use keyboard shortcuts to navigate the app. Press Ctrl + M to see the shortcuts menu",
                usersInsideInbox: "When enabled you'll be able to text teammates directly from the Inbox (a refresh is required for changes to be reflected)"
            }[name]
        },
        settingDisplay: () => (name: string): string => {
            //  Return the appropriate display based on our map
            return {
                blockAnonymousCalls: 'Block Anonymous Calls',
                onlyReceiveAssigned: 'Only Receive Assigned Notifications',
                conversationAssignment: 'Conversation Assignment',
                showMessagePreview: 'Show Message Preview',
                unassignOnClose: 'Unassign Conversation on Close',
                showCreditUsage: 'Show Credit Usage',
                hotKeysEnabled: 'Enable Shortcuts',
                usersInsideInbox: 'Text Teammates from Inbox'
            }[name]
        },
        anonCallForwardingSetting(_, getters): Setting {
            if (!getters.settings) return null

            const setting = getters.settings.find(setting => setting.name === 'callForwardBlockingAnonymousLeads')

            return setting
        },
        inboxSettings(state: State): Setting[] {
            //  Grab our settings
            const settings = state.settings

            //  We only want a specific subset
            const subset = ['conversationAssignment', 'onlyReceiveAssigned', 'unassignOnClose', 'usersInsideInbox',]

            //  Grab our subset
            const filtered = settings.filter((x: Setting) => {
                //  Only include settings that have names in our subset
                return subset.includes(x.name)
            })

            //  Return our filtered
            return filtered
        },
        settingGet: (getters) => (name: string): Setting => {
            // If the name is null, return null
            if (!name) return null

            // Find the setting by name
            let setting = getters.settings?.find(s => s.name === name)

            // Return the settings or null if not found
            return setting
        },
        settings(state: State): Setting[] {
            return state.settings
        },
        signature(_, getters): Setting {
            //  Find our setting for message signatures
            const signature = getters.settingGet('messageSignature')

            //  If it wasn't found, return null
            if (!signature) return null

            //  Else, we have to make sure we unescape the backslash in the data
            const data = signature.data

            //  Unescape any double backslashes
            signature.data = data.replace('\\n', '\n')

            //  Return our escaped signature
            return signature
        },
        workspaceSettings(state: State, getters): Setting[] {
            //  Grab our settings
            const settings = state.settings

            //  If no settings, return empty array
            if (!settings) return []

            //  Grab our inbox settings
            const inboxSettings = getters.inboxSettings

            //  If no inbox settings, return empty array
            if (!inboxSettings) return []

            //  A setting is only considered a "Workspace Setting" if it has a display name AND is not inbox
            const filtered = settings.filter((x: Setting) => {
                //  Check if this setting has a name
                const name = getters.settingDisplay(x.name)

                //  Make sure this is NOT an inbox setting
                const nonInbox = !inboxSettings.includes(x)

                //  Return if both are true
                return name && nonInbox
            })

            //  Return our filtered settings
            return filtered
        }
    }
}
